/**
 * v4.1 Phase 38.4 — Comments sidebar.
 *
 * Structured-document-only surface. Toggled from a "Comments" button
 * in the editor header (see DocumentEditor). Renders threads grouped
 * by sectionId; supports create / reply / resolve / reopen / delete
 * inline. No CodeMirror gutter integration in this scaffold — a
 * simple header toggle avoids touching editor internals; a gutter
 * integration is tracked as tech debt for a later phase.
 *
 * Gated by COLLAB_MODE_ENABLED at the caller; this component assumes
 * the caller has already checked.
 */

import { useEffect, useMemo, useState } from "react";
import {
  createComment,
  deleteComment,
  listComments,
  resolveComment,
  reopenComment,
} from "../../services/comments.js";
import { useStore } from "../../store/useStore.js";
import { getUserInfo } from "../../services/auth.js";
import "./CommentsSidebar.css";

function displayAuthor(sub, callerInfo) {
  if (!sub) return "—";
  if (callerInfo?.sub === sub && callerInfo?.email) {
    return `${callerInfo.email} (you)`;
  }
  if (sub.length <= 10) return sub;
  return `${sub.slice(0, 4)}…${sub.slice(-4)}`;
}

function CommentBubble({ comment, onReply, onResolve, onReopen, onDelete, isOwner, callerSub, callerInfo }) {
  const canDelete = isOwner || comment.authorId === callerSub;
  return (
    <div className={`comment-bubble${comment.resolved ? " comment-bubble--resolved" : ""}`}>
      <div className="comment-author">{displayAuthor(comment.authorId, callerInfo)}</div>
      <div className="comment-body">{comment.content}</div>
      <div className="comment-actions">
        {onReply && (
          <button type="button" className="btn-link" onClick={onReply}>
            Reply
          </button>
        )}
        {onResolve && !comment.resolved && (
          <button type="button" className="btn-link" onClick={onResolve}>
            Resolve
          </button>
        )}
        {onReopen && comment.resolved && (
          <button type="button" className="btn-link" onClick={onReopen}>
            Reopen
          </button>
        )}
        {canDelete && (
          <button type="button" className="btn-link-danger" onClick={onDelete}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

function Thread({
  thread,
  projectId,
  filename,
  isOwner,
  callerSub,
  callerInfo,
  onChange,
}) {
  const [replyContent, setReplyContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const pushToast = useStore((s) => s.pushToast);

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    setSubmitting(true);
    try {
      await createComment(projectId, filename, {
        sectionId: thread.sectionId,
        content: replyContent.trim(),
        parentId: thread.commentId,
      });
      setReplyContent("");
      pushToast("Reply posted", "success");
      onChange();
    } catch (err) {
      pushToast(err.message || "Failed to post reply", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="comment-thread">
      <CommentBubble
        comment={thread}
        callerSub={callerSub}
        callerInfo={callerInfo}
        isOwner={isOwner}
        onResolve={async () => {
          try {
            await resolveComment(projectId, filename, thread.commentId);
            onChange();
          } catch (err) {
            pushToast(err.message, "error");
          }
        }}
        onReopen={async () => {
          try {
            await reopenComment(projectId, filename, thread.commentId);
            onChange();
          } catch (err) {
            pushToast(err.message, "error");
          }
        }}
        onDelete={async () => {
          try {
            await deleteComment(projectId, filename, thread.commentId);
            onChange();
          } catch (err) {
            pushToast(err.message, "error");
          }
        }}
      />
      {(thread.replies || []).map((r) => (
        <CommentBubble
          key={r.commentId}
          comment={r}
          callerSub={callerSub}
          callerInfo={callerInfo}
          isOwner={isOwner}
          onDelete={async () => {
            try {
              await deleteComment(projectId, filename, r.commentId);
              onChange();
            } catch (err) {
              pushToast(err.message, "error");
            }
          }}
        />
      ))}
      {!thread.resolved && (
        <div className="comment-reply-form">
          <textarea
            className="comment-reply-input"
            placeholder="Reply…"
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            rows={2}
            disabled={submitting}
          />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleReply}
            disabled={submitting || !replyContent.trim()}
          >
            Reply
          </button>
        </div>
      )}
    </div>
  );
}

export default function CommentsSidebar({
  projectId,
  filename,
  open,
  onClose,
  isOwner,
  callerSub,
}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newSection, setNewSection] = useState("");
  const [newContent, setNewContent] = useState("");
  const [posting, setPosting] = useState(false);
  const pushToast = useStore((s) => s.pushToast);
  const callerInfo = useMemo(() => getUserInfo(), []);

  const refresh = useMemo(
    () => async () => {
      if (!projectId || !filename) return;
      try {
        const data = await listComments(projectId, filename);
        setGroups(data || []);
      } catch (err) {
        pushToast(err.message || "Failed to load comments", "error");
      }
    },
    [projectId, filename, pushToast],
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await refresh();
      if (cancelled) return;
      // Mark loading false via a microtask so the lint rule (which
      // flags sync setState inside effect bodies) is happy.
      queueMicrotask(() => {
        if (!cancelled) setLoading(false);
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [open, refresh]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newSection.trim() || !newContent.trim()) return;
    setPosting(true);
    try {
      await createComment(projectId, filename, {
        sectionId: newSection.trim(),
        content: newContent.trim(),
      });
      pushToast("Comment posted", "success");
      setNewContent("");
      await refresh();
    } catch (err) {
      pushToast(err.message || "Failed to post comment", "error");
    } finally {
      setPosting(false);
    }
  };

  if (!open) return null;

  return (
    <aside className="comments-sidebar" role="complementary" aria-label="Comments">
      <header className="comments-sidebar-header">
        <h3>Comments</h3>
        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          aria-label="Close comments"
        >
          ✕
        </button>
      </header>
      <div className="comments-sidebar-body">
        {loading ? (
          <p className="empty-hint">Loading…</p>
        ) : groups.length === 0 ? (
          <p className="empty-hint">
            No comments yet. Post one below to start a thread on any section.
          </p>
        ) : (
          groups.map((g) => (
            <section key={g.sectionId} className="comment-section-group">
              <h4 className="comment-section-heading">{g.sectionId}</h4>
              {(g.threads || []).map((t) => (
                <Thread
                  key={t.commentId}
                  thread={t}
                  projectId={projectId}
                  filename={filename}
                  isOwner={isOwner}
                  callerSub={callerSub}
                  callerInfo={callerInfo}
                  onChange={refresh}
                />
              ))}
            </section>
          ))
        )}
      </div>
      <form className="comments-sidebar-new" onSubmit={handleCreate}>
        <input
          type="text"
          className="member-add-identifier"
          placeholder="Section (e.g. Executive Summary)"
          value={newSection}
          onChange={(e) => setNewSection(e.target.value)}
          disabled={posting}
        />
        <textarea
          className="comment-reply-input"
          placeholder="Add a comment…"
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          rows={3}
          disabled={posting}
        />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={posting || !newSection.trim() || !newContent.trim()}
        >
          {posting ? "Posting…" : "Post comment"}
        </button>
      </form>
    </aside>
  );
}
